import React, { useMemo, useState } from 'react';
import api from '../../../services/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { Save, Loader2 } from 'lucide-react';
import { fmt, gbErr, MAPS } from './gbAdminUtils';

const cell = 'h-8 w-full rounded-md border px-1.5 text-center text-xs font-mono';

const FEATURES = [
  { key: 'terraces', icon: '🪜', label: 'Solo em terraços' },
  { key: 'floors', icon: '🏗️', label: 'Dois andares com buracos' },
  { key: 'archipelago', icon: '🧩', label: 'Arquipélago (ilhas grandes)' },
  { key: 'islands', icon: '🏝️', label: 'Ilhas flutuantes' },
  { key: 'islands_hard', icon: '💀', label: 'Ilhas mínimas' },
];

export default function GbMissions({ seasonId, config, onSaved }) {
  const [nodes, setNodes] = useState(() => JSON.parse(JSON.stringify(config.missions?.nodes || [])));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const set = (i, key, value) => setNodes((ns) => ns.map((n, k) => (k === i ? { ...n, [key]: value } : n)));

  const saldo = useMemo(() => {
    const custo = nodes.reduce((s, n) => s + Number(n.cost || 0), 0);
    const ganho = nodes.reduce((s, n) => s + Number(n.coin_reward || Number(n.cost || 0) * 2), 0);
    return { custo, ganho, liquido: ganho - custo };
  }, [nodes]);

  const save = async () => {
    if (reason.trim().length < 3) { toast.error('Escreva o motivo da alteração.'); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/gb/season/${seasonId}/config`, {
        reason: reason.trim(), config: { missions: { nodes } },
      });
      toast.success('Fases atualizadas.');
      setReason('');
      await onSaved();
    } catch (e) { toast.error(gbErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="gb-admin-missions">
      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <div className="rounded-xl border p-2">
          <p className="text-[10px] uppercase text-slate-400">Entradas somadas</p>
          <p className="font-black text-rose-600">💎 {fmt(saldo.custo)}</p>
        </div>
        <div className="rounded-xl border p-2">
          <p className="text-[10px] uppercase text-slate-400">Prêmios somados (3★)</p>
          <p className="font-black text-amber-600">🪙 {fmt(saldo.ganho)}</p>
        </div>
        <div className="rounded-xl border p-2">
          <p className="text-[10px] uppercase text-slate-400">Saldo se tirar 3★ em tudo</p>
          <p className="font-black text-slate-700">
            🪙 {fmt(saldo.ganho)} − 💎 {fmt(saldo.custo)}
          </p>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr>
              {['Fase', 'Nome', 'Mapa', 'Entrada 💎', 'Prêmio 🪙', 'Total 3★', 'HP oponente', 'Precisão', 'Vento', 'Cenário'].map((h) => (
                <th key={h} className="px-2 py-2 text-left font-black">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {nodes.map((n, i) => (
              <tr key={n.node} className="border-t" data-testid={`gb-node-row-${n.node}`}>
                <td className="px-2 py-1 font-black text-slate-600">{n.node}</td>
                <td className="px-2 py-1">
                  <input value={n.name || ''} onChange={(e) => set(i, 'name', e.target.value)}
                    className="h-8 w-28 rounded-md border px-1.5 text-xs" />
                </td>
                <td className="px-2 py-1">
                  <select value={n.map} onChange={(e) => set(i, 'map', e.target.value)}
                    className="h-8 rounded-md border bg-white px-1 text-xs">
                    {Object.entries(MAPS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </td>
                {[['cost', 14], ['coin_reward', 16]].map(([k, w]) => (
                  <td key={k} className="px-2 py-1">
                    <input type="number" min={0} value={n[k]} data-testid={`gb-node-${n.node}-${k}`}
                      onChange={(e) => set(i, k, Number(e.target.value))} className={cell} style={{ width: w * 4 }} />
                  </td>
                ))}
                <td className="px-2 py-1 text-center font-black text-emerald-600"
                  data-testid={`gb-node-${n.node}-total`}>
                  {fmt(Number(n.coin_reward || Number(n.cost || 0) * 2))}
                </td>
                <td className="px-2 py-1">
                  <input type="number" min={0} value={n.bot_hp} data-testid={`gb-node-${n.node}-bot_hp`}
                    onChange={(e) => set(i, 'bot_hp', Number(e.target.value))} className={cell} style={{ width: 64 }} />
                </td>
                <td className="px-2 py-1">
                  <input type="number" step={0.01} min={0.1} max={0.98} value={n.bot_accuracy}
                    onChange={(e) => set(i, 'bot_accuracy', Number(e.target.value))} className={cell} style={{ width: 60 }} />
                </td>
                <td className="px-2 py-1">
                  <input type="number" min={0} value={n.wind_max}
                    onChange={(e) => set(i, 'wind_max', Number(e.target.value))} className={cell} style={{ width: 56 }} />
                </td>
                <td className="px-2 py-1">
                  <div className="flex gap-1">
                    {FEATURES.map((f) => {
                      const on = (n.features || []).includes(f.key);
                      return (
                        <button key={f.key} title={f.label}
                          data-testid={`gb-node-${n.node}-feat-${f.key}`}
                          onClick={() => {
                            const atuais = new Set(n.features || []);
                            if (on) atuais.delete(f.key); else atuais.add(f.key);
                            const lista = FEATURES.filter((x) => atuais.has(x.key)).map((x) => x.key);
                            setNodes((ns) => ns.map((x, k) => (k === i
                              ? { ...x, features: lista, traps: false } : x)));
                          }}
                          className={`inline-flex h-8 w-8 items-center justify-center rounded-md border text-sm ${
                            on ? 'border-amber-400 bg-amber-50' : 'text-slate-300 opacity-50'}`}>
                          {f.icon}
                        </button>
                      );
                    })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-orange-200 bg-orange-50 p-3">
        <label className="min-w-[240px] flex-1 text-xs font-bold text-slate-600">
          <span className="block">Motivo da alteração</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="gb-missions-reason"
            placeholder="Ex.: fases 9 a 12 estavam difíceis demais"
            className="mt-1 h-9 w-full rounded-lg border px-2 text-sm" />
        </label>
        <Button size="sm" disabled={busy} onClick={save} data-testid="gb-missions-save"
          className="bg-emerald-600 text-white hover:bg-emerald-700">
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
          Salvar fases
        </Button>
      </div>
    </div>
  );
}
